import { DELIVERY_TRIP_STATUS } from "../../../../data/enums";

type DeliveryTripForStatusValidation = {
    is_exported?: boolean;
    is_imported?: boolean;
    is_first_storage_keeper_exported?: boolean;
    is_second_storage_keeper_exported?: boolean;
    is_first_storage_keeper_imported?: boolean;
    is_second_storage_keeper_imported?: boolean;
    normalized_items?: any[];
    delivery_products?: any[];
    items?: any[];
};

const getTripItems = (trip: DeliveryTripForStatusValidation): any[] => {
    if (Array.isArray(trip.normalized_items) && trip.normalized_items.length > 0) {
        return trip.normalized_items;
    }
    return trip.delivery_products || trip.items || [];
};

const itemBelongsToStorage = (item: any, storageNumber: 1 | 2) => {
    const storage = String(item?.product?.storage || item?.material?.storage || "");
    return new RegExp(`kho\\s*${storageNumber}`, "i").test(storage);
};

/**
 * Returns the reason a delivery-trip status change must be blocked, if any.
 * A trip can draw goods from both warehouses, so each involved keeper must
 * independently confirm the export/import operation.
 */
export const getDeliveryTripStatusValidationError = (
    trip: DeliveryTripForStatusValidation,
    nextStatus: string,
): string | null => {
    const items = getTripItems(trip);
    const hasKho1 = items.some((item) => itemBelongsToStorage(item, 1));
    const hasKho2 = items.some((item) => itemBelongsToStorage(item, 2));
    const exportedKho1 = trip.is_first_storage_keeper_exported ?? trip.is_exported ?? false;
    const exportedKho2 = trip.is_second_storage_keeper_exported ?? trip.is_exported ?? false;
    const missingExportStorages = [
        ...(hasKho1 && !exportedKho1 ? ["Kho 1"] : []),
        ...(hasKho2 && !exportedKho2 ? ["Kho 2"] : []),
    ];

    // Keep supporting legacy trips whose line items do not include a storage.
    if ((nextStatus === DELIVERY_TRIP_STATUS.IN_TRANSIT || nextStatus === DELIVERY_TRIP_STATUS.DELIVERED)
        && ((!hasKho1 && !hasKho2 && items.length > 0 && !trip.is_exported) || missingExportStorages.length > 0)) {
        const storages = missingExportStorages.length > 0 ? ` tại ${missingExportStorages.join(" và ")}` : "";
        return `Chưa xác nhận xuất kho${storages}.`;
    }

    const returnedItems = items.filter((item) => Number(item?.returned_quantity || 0) > 0);
    if (nextStatus !== DELIVERY_TRIP_STATUS.DELIVERED || returnedItems.length === 0) {
        return null;
    }

    const hasReturnKho1 = returnedItems.some((item) => itemBelongsToStorage(item, 1));
    const hasReturnKho2 = returnedItems.some((item) => itemBelongsToStorage(item, 2));
    const importedKho1 = trip.is_first_storage_keeper_imported ?? trip.is_imported ?? false;
    const importedKho2 = trip.is_second_storage_keeper_imported ?? trip.is_imported ?? false;
    const missingImportStorages = [
        ...(hasReturnKho1 && !importedKho1 ? ["Kho 1"] : []),
        ...(hasReturnKho2 && !importedKho2 ? ["Kho 2"] : []),
    ];

    if (missingImportStorages.length > 0 || (!hasReturnKho1 && !hasReturnKho2 && !trip.is_imported)) {
        const storages = missingImportStorages.length > 0 ? ` tại ${missingImportStorages.join(" và ")}` : "";
        return `Có hàng trả về chưa được nhập kho${storages}.`;
    }

    return null;
};

const editableDeliveryTripStatuses = [
    { text: "Chờ xử lý", id: DELIVERY_TRIP_STATUS.PENDING },
    { text: "Đang vận chuyển", id: DELIVERY_TRIP_STATUS.IN_TRANSIT },
    { text: "Đã vận chuyển", id: DELIVERY_TRIP_STATUS.DELIVERED },
];

export const getDeliveryTripStatusMenu = (status?: string) => {
    switch (String(status || "").toUpperCase()) {
        case DELIVERY_TRIP_STATUS.SCHEDULED:
            return [{ text: "Đã lên lịch", id: DELIVERY_TRIP_STATUS.SCHEDULED }];
        case DELIVERY_TRIP_STATUS.RETURNED:
            return [{ text: "Đã hoàn trả", id: DELIVERY_TRIP_STATUS.RETURNED }];
        case DELIVERY_TRIP_STATUS.CANCELLED:
            return [{ text: "Đã huỷ", id: DELIVERY_TRIP_STATUS.CANCELLED }];
        default:
            return editableDeliveryTripStatuses;
    }
};
